<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { format, Notify, QFile } from 'quasar';
import { config } from '@/config';
import { getToken } from '@/helpers/authUtils.ts';
import axios from 'axios';
import { FileDto } from '@/generated/graphql';

const { humanStorageSize } = format;
const ACCEPTED_FILE_FORMATS = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv';
const MAX_FILE_SIZE = 100 * 1024 * 1024;

const MAX_FILE_SIZE_FORMATTED = humanStorageSize(MAX_FILE_SIZE);

interface UploadedFile {
  id: string;
  ownerId: string | null;
  userId: string;
  companyId: string;
  sourceType: string | null;
  size: string;
  mimeType: string;
  name: string;
  deleteAfterRead: boolean;
  createdAt: string;
  updatedAt: string;
}

const props = defineProps<{
  companyId: string;
  disabled?: boolean;
  existingFiles?: Array<FileDto>;
}>();

const emit = defineEmits(['update:fileIds', 'uploading']);

const uploadedFileIds = ref<string[]>([]);
const existingFileIds = ref<string[]>([]);
const isUploadingFiles = ref<boolean>(false);
// Track the total size of uploaded files in bytes
const uploadedSize = ref<number>(0);
const fileNames = ref<Map<string, string>>(new Map());
const fileSizes = ref<Map<string, number>>(new Map());
const isDraggingOver = ref<boolean>(false);
const selectedFiles = ref<File[]>([]);
const pendingFiles = ref<File[]>([]);
const fileInput = ref<QFile | null>(null);

// Computed property to check if the uploader is disabled
const isDisabled = computed(() => props.disabled || isUploadingFiles.value);

// Computed property for the formatted total uploaded size
const formattedTotalSize = computed(() => humanStorageSize(uploadedSize.value));

/**
 * Gets the file name for a given file ID
 * @param fileId - The ID of the file
 * @param index - The index of the file in the list (used as fallback)
 * @returns The file name or a generic name if not found
 */
const getFileName = (fileId: string, index: number): string => {
  // Return the stored file name if available, otherwise return a generic name
  return fileNames.value.get(fileId) || `File ${index + 1}`;
};

/**
 * Validates files and adds them to the pending files list
 * @param files - Array of selected files
 */
const validateAndStorePendingFiles = (files: File[]) => {
  if (!files || !files.length || !props.companyId) return;

  // Check for files that exceed the individual size limit
  const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
  if (oversizedFiles.length > 0) {
    const fileNames = oversizedFiles.map(file => file.name).join(', ');
    Notify.create({
      message: `File size exceeds the limit of ${MAX_FILE_SIZE_FORMATTED}: ${fileNames}`,
      color: 'negative',
      position: 'top',
    });

    // If all files are oversized, return early
    if (oversizedFiles.length === files.length) {
      return;
    }

    // Continue with files that are within the size limit
    files = files.filter(file => file.size <= MAX_FILE_SIZE);
  }

  // Calculate the total size of all files to be uploaded
  const totalNewSize = files.reduce((total, file) => total + file.size, 0);

  // Check if adding these files would exceed the maximum total size
  if (uploadedSize.value + totalNewSize > MAX_FILE_SIZE) {
    Notify.create({
      message: `Adding these files would exceed the maximum total size of ${MAX_FILE_SIZE_FORMATTED}. Current size: ${formattedTotalSize.value}`,
      color: 'negative',
      position: 'top',
    });
    return;
  }

  // Add files to a pending files list
  pendingFiles.value = [...pendingFiles.value, ...files];

  // Display the files in the UI (without uploading)
  files.forEach(file => {
    // Generate a temporary ID for display purposes
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Store file name and size for display
    fileNames.value.set(tempId, file.name);
    fileSizes.value.set(tempId, file.size);

    // Add to uploadedFileIds for display purposes
    uploadedFileIds.value.push(tempId);

    // Update total size
    uploadedSize.value += file.size;
  });

  // Emit the updated file IDs to the parent component
  emit('update:fileIds', uploadedFileIds.value);

  // Clear the selected files input
  selectedFiles.value = [];
};

/**
 * Handles the actual upload of files to the server
 * @returns Promise<string[]> - Array of uploaded file IDs (both new and existing)
 */
const uploadPendingFiles = async (): Promise<string[]> => {
  // If there are no pending files but we have existing files, just return the existing file IDs
  if (!pendingFiles.value.length) {
    return existingFileIds.value;
  }

  if (!props.companyId) return uploadedFileIds.value;

  // Set uploading state
  isUploadingFiles.value = true;
  emit('uploading', true);

  try {
    // Create FormData for manual upload
    const formData = new FormData();
    formData.append('companyId', props.companyId);

    // Add each file to FormData
    pendingFiles.value.forEach(file => {
      formData.append('files', file);
    });

    // Manual upload using axios
    const response = await axios.post(
      config.filestorage,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'enty-auth': getToken(),
        },
      },
    );

    // Process response to get file IDs
    if (response.data && Array.isArray(response.data)) {
      const uploadedFiles = response.data;

      // Store the existing files' sizes and names
      const existingFileSizes = new Map<string, number>();
      const existingFileNames = new Map<string, string>();

      existingFileIds.value.forEach(fileId => {
        const size = fileSizes.value.get(fileId);
        const name = fileNames.value.get(fileId);
        if (size !== undefined) existingFileSizes.set(fileId, size);
        if (name !== undefined) existingFileNames.set(fileId, name);
      });

      // Clear temporary IDs and sizes
      uploadedFileIds.value = [];
      uploadedSize.value = 0;
      fileNames.value.clear();
      fileSizes.value.clear();

      // Restore existing files' data
      existingFileIds.value.forEach(fileId => {
        const size = existingFileSizes.get(fileId);
        const name = existingFileNames.get(fileId);
        if (size !== undefined) {
          fileSizes.value.set(fileId, size);
          uploadedSize.value += size;
        }
        if (name !== undefined) fileNames.value.set(fileId, name);
      });

      // Process newly uploaded files
      const newFileIds = uploadedFiles.map((file: UploadedFile) => {
        const fileId = file.id;
        const fileSize = parseInt(file.size, 10) || 0;

        // Add to the total uploaded size
        uploadedSize.value += fileSize;

        // Store the file size in the Map for later retrieval when removing files
        fileSizes.value.set(fileId, fileSize);

        // Store the file name with its ID
        fileNames.value.set(fileId, file.name);

        return fileId;
      });

      // Combine existing and new file IDs
      const allFileIds = [...existingFileIds.value, ...newFileIds];

      // Set the combined file IDs for UI display
      uploadedFileIds.value = allFileIds;

      // Clear pending files after successful upload
      pendingFiles.value = [];

      // Return all file IDs (both existing and newly uploaded)
      return allFileIds;
    }

    return existingFileIds.value;
  } catch (error) {
    console.error('Error uploading files:', error);
    Notify.create({
      message: 'Failed to upload files. Please try again.',
      color: 'negative',
      position: 'top',
    });
    return existingFileIds.value;
  } finally {
    isUploadingFiles.value = false;
    emit('uploading', false);
  }
};

/**
 * Triggers file validation and storage when files are selected
 */
const onFilesSelected = () => {
  if (selectedFiles.value && selectedFiles.value.length > 0) {
    validateAndStorePendingFiles([...selectedFiles.value]);
  }
};

/**
 * Clears the selected files
 */
const clearSelectedFiles = () => {
  selectedFiles.value = [];
};

/**
 * Drag and drop event handlers for file upload functionality
 */

/**
 * Handles the dragenter event when files are dragged over the drop zone
 * @param event - The drag event
 */
const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  if (!isDisabled.value) {
    isDraggingOver.value = true;
  }
};

/**
 * Handles the dragover event to maintain the visual feedback
 * @param event - The drag event
 */
const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  if (!isDisabled.value) {
    isDraggingOver.value = true;
  }
};

/**
 * Handles the dragleave event when files are dragged away from the drop zone
 * @param event - The drag event
 */
const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  isDraggingOver.value = false;
};

/**
 * Handles the drop event when files are dropped onto the drop zone
 * @param event - The drag event containing the dropped files
 */
const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  isDraggingOver.value = false;

  if (isDisabled.value) {
    return;
  }

  // Check if the event has files
  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    // Convert FileList to Array for easier handling
    const droppedFiles = Array.from(event.dataTransfer.files);

    // Process the dropped files
    validateAndStorePendingFiles(droppedFiles);
  }
};

/**
 * Removes a file from the uploaded files list
 * @param index - The index of the file to remove
 */
const removeUploadedFile = (index: number) => {
  const fileId = uploadedFileIds.value[index];
  if (fileId) {
    // Get the file size from our Map
    const fileSize = fileSizes.value.get(fileId) || 0;
    if (fileSize > 0) {
      uploadedSize.value = Math.max(0, uploadedSize.value - fileSize);
      fileSizes.value.delete(fileId);
    }

    // Remove the file ID from the array
    uploadedFileIds.value.splice(index, 1);

    // If this is an existing file, remove it from existingFileIds as well
    const existingIndex = existingFileIds.value.indexOf(fileId);
    if (existingIndex !== -1) {
      existingFileIds.value.splice(existingIndex, 1);
    }

    // Check if this is a temporary ID (for pending files)
    if (fileId.startsWith('temp-')) {
      // Find the corresponding pending file and remove it
      const tempIdParts = fileId.split('-');
      const timestamp = tempIdParts[1];
      // Find the index of the pending file with this timestamp
      const pendingIndex = pendingFiles.value.findIndex(
        () => `temp-${timestamp}-` === fileId.substring(0, `temp-${timestamp}-`.length),
      );
      if (pendingIndex !== -1) {
        pendingFiles.value.splice(pendingIndex, 1);
      }
    }

    // Remove the file name from the map
    fileNames.value.delete(fileId);

    // Emit the updated file IDs to the parent component
    emit('update:fileIds', uploadedFileIds.value);
  }
};

/**
 * Clears all uploaded files and resets the total size
 */
const clearFiles = () => {
  // Clear file sizes from the Map
  fileSizes.value.clear();

  // Reset the uploaded file IDs array
  uploadedFileIds.value = [];

  // Reset the existing file IDs array
  existingFileIds.value = [];

  // Clear the file names map
  fileNames.value.clear();

  // Reset the total uploaded size
  uploadedSize.value = 0;

  // Clear selected files
  selectedFiles.value = [];

  // Clear pending files
  pendingFiles.value = [];

  emit('update:fileIds', []);
};

/**
 * Initialize the component with existing files when they are provided
 * This handles FileDto objects from the message payload
 */
const initializeWithExistingFiles = () => {
  if (props.existingFiles && props.existingFiles.length > 0) {
    clearFiles();

    let totalSize = 0;
    props.existingFiles.forEach(file => {
      const fileId = file.id;
      const fileName = file.name;
      const fileSize = typeof file.size === 'string' ? parseFloat(file.size) : Number(file.size);
      fileNames.value.set(fileId, fileName);

      fileSizes.value.set(fileId, fileSize);

      totalSize += fileSize;
    });

    // Store the file IDs both in uploadedFileIds for UI display
    // and in existingFileIds to track which files were already uploaded
    uploadedFileIds.value = props.existingFiles.map(file => file.id);
    existingFileIds.value = [...uploadedFileIds.value];

    uploadedSize.value = totalSize;

    emit('update:fileIds', uploadedFileIds.value);
  }
};

watch(() => props.existingFiles, (newFiles) => {
  if (newFiles && newFiles.length > 0) {
    initializeWithExistingFiles();
  }
}, { immediate: true });

onMounted(() => {
  if (props.existingFiles && props.existingFiles?.length > 0) {
    initializeWithExistingFiles();
  }
});

defineExpose({
  clearFiles,
  uploadPendingFiles,
  getUploadedFileIds: () => uploadedFileIds.value,
  getExistingFileIds: () => existingFileIds.value,
  getUploadedSize: () => uploadedSize.value,
  getFormattedTotalSize: () => formattedTotalSize.value,
});
</script>

<template>
  <div class="file-uploader">
    <!-- File upload area with drag & drop support -->
    <div class="col-12 q-mb-sm">
      <div
        class="file-dropzone q-pa-md"
        :class="{
          'disabled': isDisabled,
          'drag-over': isDraggingOver
        }"
        @dragenter="handleDragEnter"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
      >
        <div class="row items-center justify-center">
          <q-spinner
            v-if="isUploadingFiles"
            color="primary"
            size="md"
            class="q-mr-sm"
          />
          <q-icon
            v-else
            name="cloud_upload"
            size="md"
            color="primary"
            class="q-mr-sm upload-icon"
          />
          <div class="text-subtitle1">
            <span
              v-if="isUploadingFiles"
              class="text-weight-medium"
            >Processing files...</span>
            <template v-else>
              <span class="text-weight-medium">Drag & drop files here</span> or
              <q-btn
                flat
                dense
                color="secondary"
                label="Browse"
                :disable="isDisabled"
                @click="fileInput?.pickFiles()"
              />
            </template>
          </div>
        </div>
        <div class="text-caption text-center q-mt-xs text-grey-7">
          <span v-if="isUploadingFiles">Please wait while files are being uploaded...</span>
          <span v-else>Supported formats: PDF, CSV, Word, Excel, Images, Text (Max size: {{ MAX_FILE_SIZE_FORMATTED }})</span>
        </div>

        <q-file
          ref="fileInput"
          v-model="selectedFiles"
          multiple
          clearable
          outlined
          counter
          :accept="ACCEPTED_FILE_FORMATS"
          :disable="isDisabled"
          style="display: none"
          name="selectFilesInput"
          @update:model-value="onFilesSelected"
          @clear="clearSelectedFiles"
        >
          <template #prepend>
            <q-icon name="attach_file" />
          </template>
        </q-file>
      </div>
    </div>

    <!-- Display uploaded files -->
    <div
      v-if="uploadedFileIds.length > 0"
      class="col-12 q-mt-sm"
    >
      <div class="text-subtitle2 q-mb-xs">
        Attached files: <span class="text-caption text-grey-7">(Total size: {{ formattedTotalSize }})</span>
      </div>
      <div class="uploaded-files-container">
        <q-chip
          v-for="(fileId, index) in uploadedFileIds"
          :key="`file-${fileId}${index}`"
          removable
          color="primary"
          text-color="white"
          class="q-ma-xs"
          @remove="removeUploadedFile(index)"
        >
          <q-icon
            name="attachment"
            class="q-mr-xs"
          />
          <span class="ellipsis">{{ getFileName(fileId, index) }}</span>
        </q-chip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.file-uploader {
  width: 100%;

  .file-dropzone {
    width: 100%;
    border: 2px dashed #ccc;
    border-radius: 8px;
    background-color: #f8f8f8;
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;

    &:hover {
      border-color: var(--q-primary);
      background-color: rgba(var(--q-primary-rgb), 0.05);
    }

    &.drag-over {
      border-color: var(--q-primary);
      background-color: rgba(var(--q-primary-rgb), 0.1);
      transform: scale(1.01);
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

      .q-icon {
        transform: scale(1.2);
        color: var(--q-primary) !important;
      }
    }

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;

      &:hover {
        border-color: #ccc;
        background-color: #f8f8f8;
      }
    }

    .upload-icon {
      transition: transform 0.3s ease;
    }

    .bounce-animation {
      animation: bounce 1.5s infinite;
    }

    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-10px);
      }
      60% {
        transform: translateY(-5px);
      }
    }
  }

  .uploaded-files-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    overflow-x: auto;
    padding: 4px;
    background-color: rgba(0, 0, 0, 0.03);
    border-radius: 8px;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }
  }
}
</style>
